/** `/auth/*` — pure fetch wrappers, no session state (that's `auth/auth-service.ts`'s job). */
import { API_BASE_URL, parseOrThrow, type StoredAuthUser } from "./http";

export interface AuthResult {
  accessToken: string;
  /** Present on every response of a server that has refresh tokens (SCRUM-729). */
  refreshToken?: string;
  user: StoredAuthUser;
}

export async function loginRequest(email: string, password: string): Promise<AuthResult> {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return parseOrThrow<AuthResult>(res);
}

/** `via: "offer"` — the form was opened from «Make offer»: the server makes the account a rental company (SCRUM-647). */
export async function registerRequest(email: string, password: string, via?: "offer", displayName?: string): Promise<AuthResult> {
  const res = await fetch(`${API_BASE_URL}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // `displayName`: the name typed on the crew link's sign-up (SCRUM-976 п.1; owner, 01.11.2026: «спрашивать имя при регистрации»).
    body: JSON.stringify({ email, password, ...(via ? { via } : {}), ...(displayName?.trim() ? { displayName: displayName.trim() } : {}) }),
  });
  return parseOrThrow<AuthResult>(res);
}

/** Sign-in by email link (SCRUM-591): mails a one-time link. Always `{ ok: true }` — never reveals
 *  whether the address has an account. */
export async function requestEmailLogin(
  email: string,
  locale: string,
  offer?: { riderId: string; riderVersion: number; draft: unknown; when?: unknown; venue?: unknown }
): Promise<{ ok: true }> {
  const res = await fetch(`${API_BASE_URL}/auth/email-login/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(offer ? { email, locale, offer } : { email, locale }),
  });
  return parseOrThrow<{ ok: true }>(res);
}

/** Redeems the link from the letter; 400 when it is expired or already used. */
export async function confirmEmailLoginRequest(
  token: string
): Promise<AuthResult & { offers?: Array<{ riderId: string; riderVersion: number; offerId?: string; status: string }> }> {
  const res = await fetch(`${API_BASE_URL}/auth/email-login/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  return parseOrThrow<AuthResult & { offers?: Array<{ riderId: string; riderVersion: number; offerId?: string; status: string }> }>(res);
}

/** What a letter's link is, without spending it (SCRUM-591): a spent link lands on a way forward,
 *  with its address prefilled, not on a wall. */
export async function emailLoginStatus(token: string): Promise<{ state: string; email?: string }> {
  const res = await fetch(`${API_BASE_URL}/auth/email-login/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  return parseOrThrow<{ state: string; email?: string }>(res);
}

/** SCRUM-729: trades a refresh token for a new access + refresh pair. Plain `fetch`, never
 *  `authFetch` - this call must not trigger the very exchange it is. Throws `ApiError` on non-2xx;
 *  the caller tells "refused" (400/401/403) from "could not complete" by its status. */
export async function refreshRequest(refreshToken: string): Promise<AuthResult> {
  const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  return parseOrThrow<AuthResult>(res);
}

/** Tells the server to forget this device's refresh token. Best-effort: the caller forgets locally regardless. */
export async function logoutRequest(refreshToken: string): Promise<void> {
  await fetch(`${API_BASE_URL}/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
}
